import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { ExtraMessageWithBlurredBg } from '@sorare/core/src/components/onboarding/ExtraMessageWithBlurredBg';
import { HighlightOverlay } from '@sorare/core/src/components/onboarding/HighlightOverlay';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import useLifecycle, { LIFECYCLE } from '@sorare/core/src/hooks/useLifecycle';
import { glossary } from '@sorare/core/src/lib/glossary';

import { GalleryCardExtra } from './GalleryCardExtra';
import {
  GalleryOnboardingQuery,
  GalleryOnboardingQueryVariables,
} from './__generated__/index.graphql';

const StyledButton = styled(Button)`
  position: absolute;
  bottom: var(--quadruple-unit);
`;

const query = gql`
  query GalleryOnboardingQuery {
    currentUser {
      slug
      nbaUserProfile {
        id
        cardsCount {
          limited
          rare
          superRare
          unique
        }
      }
    }
  }
` as TypedDocumentNode<GalleryOnboardingQuery, GalleryOnboardingQueryVariables>;

export const GalleryOnboarding = () => {
  const { data } = useQuery(query);
  const [step, setStep] = useState(0);
  const isTabletAndAbove = useIsTabletAndAbove();
  const { lifecycle, update: updateLifecycle } = useLifecycle();

  // If user already own blockchain card we want to skip the scarcity onboarding
  const cardsCountSum = Object.values(
    data?.currentUser?.nbaUserProfile?.cardsCount ?? {}
  ).reduce((acc: number, count) => {
    if (typeof count === 'number') {
      return acc + count;
    }

    return acc;
  }, 0);

  if (cardsCountSum > 0 || lifecycle?.sawGalleryOnboarding) return null;

  if (step === 0) {
    return (
      <HighlightOverlay
        key="ManagerProfileGallery"
        disableBackdropClose
        onClose={() => setStep(1)}
        highlightNames={['ManagerProfileGallery']}
        extras={[
          {
            nextTo: 'ManagerProfileGallery',
            render: ({ top, left, right, bottom }) => (
              <>
                <ExtraMessageWithBlurredBg
                  style={{
                    position: 'absolute',
                    top: `calc(${top}px - var(--unit) * 9)`,
                    width: isTabletAndAbove ? '340px' : right - left,
                    left: isTabletAndAbove
                      ? `calc(${left}px + (${right - left}px - 340px) / 2)`
                      : left,
                  }}
                >
                  <LabelL>
                    <FormattedMessage
                      id="NBA.managerProfile.GalleryOnboarding"
                      defaultMessage="You can find the Cards you own here"
                    />
                  </LabelL>
                </ExtraMessageWithBlurredBg>
                <StyledButton
                  size="large"
                  color="secondary"
                  onClick={() => setStep(1)}
                  style={{
                    top: isTabletAndAbove
                      ? `calc(${bottom}px + var(--double-unit))`
                      : '',
                    width: isTabletAndAbove ? '340px' : right - left,
                    left: isTabletAndAbove
                      ? `calc(${left}px + (${right - left}px - 340px) / 2)`
                      : left,
                  }}
                >
                  <FormattedMessage {...glossary.next} />
                </StyledButton>
              </>
            ),
          },
        ]}
      />
    );
  }

  return (
    <HighlightOverlay
      key="ManagerProfileGalleryCard"
      disableBackdropClose
      onClose={() => updateLifecycle(LIFECYCLE.sawGalleryOnboarding, true)}
      highlightNames={['ManagerProfileGalleryCard']}
      extras={[
        {
          nextTo: 'ManagerProfileGalleryCard',
          render: props => <GalleryCardExtra {...props} />,
        },
      ]}
    />
  );
};
